import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  TextField,
  Box
} from '@mui/material';
import { LocalizationProvider, DateTimePicker, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { de } from 'date-fns/locale';
import { DateRangeOutlined, ScheduleOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface CampaignDateSettingsProps {
  type: 'duration' | 'execution';
  startDate?: string;
  endDate?: string;
  executionDate?: string;
  onStartDateChange?: (date: string) => void;
  onEndDateChange?: (date: string) => void;
  onExecutionDateChange?: (date: string) => void;
  startDateError?: string;
  endDateError?: string;
  executionDateError?: string;
  disabled?: boolean;
}

const CampaignDateSettings: React.FC<CampaignDateSettingsProps> = ({
  type,
  startDate,
  endDate,
  executionDate,
  onStartDateChange,
  onEndDateChange,
  onExecutionDateChange,
  startDateError,
  endDateError,
  executionDateError,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [openPickers, setOpenPickers] = React.useState({
    startDate: false,
    endDate: false,
    executionDate: false
  });
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to start of today

  const parseDate = (dateString?: string) => {
    try {
      if (dateString && dateString !== '') {
        const date = new Date(dateString);
        return Number.isNaN(date.getTime()) ? null : date;
      }
      return null;
    } catch {
      return null;
    }
  };

  const isDateDisabled = (date: Date) => {
    // Disable dates before today
    return date < today;
  };

  // Extract minute value helper function
  const extractMinuteValue = (value: any): number => {
    if (typeof value === 'number') return value;
    if (value instanceof Date) return value.getMinutes();
    return value;
  };

  // Extract hour value helper function
  const extractHourValue = (value: any): number => {
    if (typeof value === 'number') return value;
    if (value instanceof Date) return value.getHours();
    return value;
  };

  // Check if date context is today
  const isDateContextToday = (dateContext: Date | null): boolean => {
    if (!dateContext) return false;
    
    const dateOnly = new Date(dateContext);
    dateOnly.setHours(0, 0, 0, 0);
    const todayOnly = new Date();
    todayOnly.setHours(0, 0, 0, 0);
    
    return dateOnly.getTime() === todayOnly.getTime();
  };

  // Get date context for time validation
  const getDateContext = (value: any): Date | null => {
    if (executionDate) {
      return parseDate(executionDate);
    }
    
    if (value instanceof Date) {
      return value;
    }
    
    return null;
  };

  const shouldDisableExecutionTime = (value: any, view: string) => {
    // Disable minutes that are not 0 (only allow full hours)
    if (view === 'minutes') {
      const minuteValue = extractMinuteValue(value);
      return minuteValue !== 0;
    }

    // For hours, check if we're selecting a time for today
    if (view === 'hours') {
      const now = new Date();
      const currentHour = now.getHours();
      const hourValue = extractHourValue(value);
      const dateContext = getDateContext(value);

      // If selecting a time for today, disable hours that have passed or are current
      if (isDateContextToday(dateContext)) {
        return hourValue <= currentHour;
      }
    }

    return false;
  };

  const shouldDisableEndDate = (date: Date) => {
    // Disable dates before today
    if (date < today) return true;
    
    // If start date is selected, disable dates before start date
    const parsedStartDate = parseDate(startDate);
    if (parsedStartDate) {
      return date < parsedStartDate;
    }
    
    return false;
  };

  const handleDateChange = (newValue: Date | null, onChange?: (date: string) => void) => {
    try {
      const isValidDate = newValue && !Number.isNaN(newValue.getTime());
      if (isValidDate && onChange) {
        onChange(newValue.toISOString());
      } else if (onChange) {
        onChange('');
      }
    } catch (error) {
      console.warn('Invalid date:', error);
      if (onChange) onChange('');
    }
  };

  // Extract title and description helpers to avoid nested ternary
  const getTitle = () => {
    return type === 'duration' 
      ? t('campaignExperimental.create.campaignDurationTitle')
      : t('campaignExperimental.create.campaignExecutionTitle');
  };

  const getDescription = () => {
    return type === 'duration'
      ? t('campaignExperimental.create.campaignDurationDesc')
      : t('campaignExperimental.create.campaignExecutionDesc');
  };

  const getIcon = () => {
    return type === 'duration'
      ? <DateRangeOutlined color="primary" />
      : <ScheduleOutlined color="primary" />;
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {getIcon()}
          <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
            {getTitle()}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {getDescription()}
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        <Grid container spacing={3}>
          {type === 'duration' ? (
            <>
              <Grid item xs={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
                  <DatePicker
                    label={t('campaignExperimental.create.startDateLabel')}
                    open={openPickers.startDate}
                    onOpen={() => setOpenPickers(prev => ({ ...prev, startDate: true }))}
                    onClose={() => setOpenPickers(prev => ({ ...prev, startDate: false }))}
                    value={parseDate(startDate)}
                    onChange={(newValue) => handleDateChange(newValue, onStartDateChange)}
                    shouldDisableDate={isDateDisabled}
                    minDate={today}
                    disabled={disabled}
                    renderInput={(renderProps) => (
                      <TextField
                        {...renderProps}
                        fullWidth
                        disabled={disabled}
                        error={!!startDateError}
                        helperText={startDateError || t('campaignExperimental.create.startDateHelp')}
                        onClick={() => setOpenPickers(prev => ({ ...prev, startDate: true }))}
                        sx={{
                          '& .MuiInputBase-input': {
                            cursor: 'pointer'
                          },
                          '& .MuiInputBase-root': {
                            cursor: 'pointer'
                          }
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
                  <DatePicker
                    label={t('campaignExperimental.create.endDateLabel')}
                    open={openPickers.endDate}
                    onOpen={() => setOpenPickers(prev => ({ ...prev, endDate: true }))}
                    onClose={() => setOpenPickers(prev => ({ ...prev, endDate: false }))}
                    value={parseDate(endDate)}
                    onChange={(newValue) => handleDateChange(newValue, onEndDateChange)}
                    shouldDisableDate={shouldDisableEndDate}
                    minDate={parseDate(startDate) || today}
                    disabled={disabled}
                    renderInput={(renderProps) => (
                      <TextField
                        {...renderProps}
                        fullWidth
                        disabled={disabled}
                        error={!!endDateError}
                        helperText={endDateError || t('campaignExperimental.create.endDateHelp')}
                        onClick={() => setOpenPickers(prev => ({ ...prev, endDate: true }))}
                        sx={{
                          '& .MuiInputBase-input': {
                            cursor: 'pointer'
                          },
                          '& .MuiInputBase-root': {
                            cursor: 'pointer'
                          }
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
                <DateTimePicker
                  ampm={false}
                  label={t('campaignExperimental.create.executionDateTimeLabel')}
                  open={openPickers.executionDate}
                  onOpen={() => setOpenPickers(prev => ({ ...prev, executionDate: true }))}
                  onClose={() => setOpenPickers(prev => ({ ...prev, executionDate: false }))}
                  value={parseDate(executionDate)}
                  minutesStep={60}
                  shouldDisableTime={shouldDisableExecutionTime}
                  onChange={(newValue) => handleDateChange(newValue, onExecutionDateChange)}
                  shouldDisableDate={isDateDisabled}
                  minDateTime={new Date()}
                  disabled={disabled}
                  renderInput={(renderProps) => (
                    <TextField
                      {...renderProps}
                      fullWidth
                      disabled={disabled}
                      error={!!executionDateError}
                      helperText={executionDateError || t('campaignExperimental.create.executionDateTimeHelp')}
                      onClick={() => setOpenPickers(prev => ({ ...prev, executionDate: true }))}
                      sx={{
                        '& .MuiInputBase-input': {
                          cursor: 'pointer'
                        },
                        '& .MuiInputBase-root': {
                          cursor: 'pointer'
                        }
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default CampaignDateSettings;
